/**
 * RESPONSIBILITY: draw one contact request, with its tick or redo and its note.
 *
 * A waiting request offers the tick (archive it); an archived one offers redo (back to the
 * queue). One button each way, and the note button on both. Presentation only: what each
 * button DOES is handed in.
 *
 * The visitor's own words are rendered as text, never as HTML - they came from the public
 * form, and nothing about a stranger's message should be trusted to be markup.
 */
import { useState } from 'react';
import type { RequestsSection } from '@app/sections';
import type { ContactRequest } from './useRequests';
import { NoteEditor } from './NoteEditor';

export interface RequestCardProps {
  c: RequestsSection;
  r: ContactRequest;
  onDone: (id: number, done: boolean) => void;
  onNote: (id: number, note: string) => Promise<unknown>;
}

const when = (t: number) => new Date(t).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

export function RequestCard({ c, r, onDone, onNote }: RequestCardProps) {
  const [editing, setEditing] = useState(false);
  const archived = r.handledAt !== null;
  const meta = [r.describesYou, r.heardAboutUs, r.replyBy].filter(Boolean).join(' · ');

  return (
    <article className={`rq-card${archived ? ' is-archived' : ''}`}>
      <header className="rq-head">
        <h3>{[r.firstName, r.lastName].filter(Boolean).join(' ')}</h3>
        <time dateTime={new Date(r.createdAt).toISOString()}>{when(r.createdAt)}</time>
      </header>

      <p className="rq-contact">
        <a href={`mailto:${r.email}`}>{r.email}</a>
        {r.phone ? <a href={`tel:${r.phone}`}>{r.phone}</a> : null}
      </p>
      {meta ? <p className="rq-meta">{meta}</p> : null}
      {r.wants.length ? <ul className="rq-wants">{r.wants.map((w) => <li key={w}>{w}</li>)}</ul> : null}
      {r.message ? <p className="rq-message">{r.message}</p> : null}
      {r.note && !editing ? <p className="rq-note">{r.note}</p> : null}

      {editing ? (
        <NoteEditor
          c={c} initial={r.note} onCancel={() => setEditing(false)}
          onSave={async (n) => { await onNote(r.id, n); setEditing(false); }}
        />
      ) : (
        <div className="rq-actions">
          {archived ? (
            <button className="btn btn-ghost rq-redo" type="button" onClick={() => onDone(r.id, false)}>
              <span aria-hidden="true">↺</span> {c.redoLabel}
            </button>
          ) : (
            <button className="btn rq-tick" type="button" onClick={() => onDone(r.id, true)}>
              <span aria-hidden="true">✓</span> {c.doneLabel}
            </button>
          )}
          <button className="btn btn-ghost" type="button" onClick={() => setEditing(true)}>
            <span aria-hidden="true">✎</span> {c.noteLabel}
          </button>
        </div>
      )}
    </article>
  );
}
